import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { palette } from '../../../styles/tokens';
import type { ConversationSummary, StatusDot } from '../types';

// §T.157 — inbox status line, derive-on-read. The zero-arg RPC returns
// status_kind + dot + raw amount/title/member_count keyed by thread id (both
// conversation and group targets). Admin has no i18n runtime, so labels are
// composed here as plain English strings (admin convention).

export function dotColor(dot: StatusDot): string {
  switch (dot) {
    case 'green': return palette.green;
    case 'red': return palette.red;
    case 'blue': return palette.accent;
    case 'amber': return palette.amber;
    case 'grey': default: return palette.g400;
  }
}

function money(amount: number | null): string {
  if (amount == null) return '';
  return amount.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

export function statusLabel(s: ConversationSummary): string | null {
  const amount = money(s.amount);
  const title = s.title ?? '';
  switch (s.status_kind) {
    case 'invoice_paid': return `Invoice paid${amount ? ` · ${amount}` : ''}`;
    case 'invoice_overdue': return `Invoice overdue${amount ? ` · ${amount}` : ''}`;
    case 'invoice_sent': return `Invoice sent${amount ? ` · ${amount}` : ''}`;
    case 'job_complete': return title ? `Job complete · ${title}` : 'Job complete';
    case 'job_in_progress': return title ? `In progress · ${title}` : 'In progress';
    case 'job_new': return title ? `New job · ${title}` : 'New job';
    case 'job_scheduled': return title ? `Scheduled · ${title}` : 'Scheduled';
    case 'quote_accepted': return 'Quote accepted';
    case 'quote_declined': return 'Quote declined';
    case 'quote_sent': return `Quote sent${amount ? ` · ${amount}` : ''}`;
    case 'group': return `${s.member_count ?? 0} members`;
    case 'awaiting_reply': return 'Awaiting reply';
    default: return null;
  }
}

/**
 * Fetches per-thread status summaries (derive-on-read). Refetches whenever
 * `reloadKey` changes — pass the inbox rows so a new message / status change
 * refreshes the line.
 */
export function useConversationSummaries(reloadKey?: unknown) {
  const [summaries, setSummaries] = useState<Record<string, ConversationSummary>>({});
  const mountedRef = useRef(true);

  const fetchSummaries = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_conversation_summaries');
    if (!mountedRef.current || error) return;
    if (data?.success && Array.isArray(data.summaries)) {
      const map: Record<string, ConversationSummary> = {};
      for (const s of data.summaries as ConversationSummary[]) map[s.id] = s;
      setSummaries(map);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot derive-on-read fetch; setSummaries runs in an awaited callback, standard data-on-mount pattern
    void fetchSummaries();
    return () => { mountedRef.current = false; };
  }, [fetchSummaries, reloadKey]);

  return { summaries, refresh: fetchSummaries };
}
